import React, { FC, useMemo, useState } from 'react'

import { SearchBar } from '../../Components/Search/SearchBar'
import TabSwitcher, { TabSwitcherProps } from '../../Components/TabSwitcher/TabSwitcher'
import FoodModal from '../JournalPage/components/Modals/FoodModal/FoodModal'
import RecipeModal from '../JournalPage/components/Modals/RecipeModal/RecipeModal'

import FoodContainer from './FoodCardContainer'
import RecipeCardContainer from './RecipeCardContainer'

import {
  BackToTopButton,
  InfoPlaceholder,
  PageLayout,
  SectionList,
  getSectionListSortedByNormalizedName
} from 'Components'
import { addNormalizedNameProp } from 'Components/SectionList/sortItemList'
import SidebarUserFood from 'Components/SidebarUserFood/SidebarUserFood'
import { CoreBackendRecipe, Food, QuantifiedFood, Recipe } from 'Models'
import { Utils } from 'Utils'

type OnCardClickCallback = (id: string) => void

type MyEatablesPageProps = {
  // foods
  userFoods: Food[]
  isFoodsLoading: boolean
  didLoadUserFoods: boolean
  modalFoodId?: string
  onFoodCardClick: OnCardClickCallback
  onFoodModalClose: () => void
  // recipes
  userRecipes: CoreBackendRecipe[]
  isRecipesLoading: boolean
  didLoadUserRecipes: boolean
  modalRecipeId?: string
  onRecipeCardClick: OnCardClickCallback
  onRecipeModalClose: () => void
}

const MyEatablesPage: FC<MyEatablesPageProps> = ({
  // foods
  userFoods,
  isFoodsLoading,
  didLoadUserFoods,
  modalFoodId,
  onFoodCardClick,
  onFoodModalClose,

  // recipes
  userRecipes,
  isRecipesLoading,
  didLoadUserRecipes,
  modalRecipeId,
  onRecipeCardClick,
  onRecipeModalClose
}: MyEatablesPageProps) => {
  const isInitialLoadingFoods = isFoodsLoading && !didLoadUserFoods
  const isInitialLoadingRecipes = isRecipesLoading && !didLoadUserRecipes

  const [searchQuery, setSearchQuery] = useState('')
  const normalizedQuery = searchQuery.trim().toLowerCase()

  const itemTypeNames: Record<string, string> = {
    foods: 'Nahrungsmittel',
    recipes: 'Rezepte'
  }

  const NoEntriesPlaceholder: FC<{ itemTypeName: string }> = ({ itemTypeName }) => {
    return <InfoPlaceholder text={`Sie haben noch keine eigenen ${itemTypeName} erstellt.`} />
  }

  const NoResultsPlaceholder: FC<{ itemTypeName: string }> = ({ itemTypeName }) => {
    return (
      <InfoPlaceholder title="Keine passenden Ergebnisse" text={`Keine passenden eigenen ${itemTypeName} gefunden.`} />
    )
  }

  const LoadingNotification: FC<{ itemTypeName: string }> = ({ itemTypeName }) => {
    return (
      <div className="text-center mt-2">
        <p>{itemTypeName} werden geladen …</p>
      </div>
    )
  }

  const selectedFood: QuantifiedFood | undefined = useMemo(() => {
    const food = userFoods.find(({ id }) => id === modalFoodId)
    if (!food) return undefined
    const { quantity, unit } = Utils.defaultEatableAmountForFood(food)
    return {
      food,
      quantity,
      unit
    }
  }, [userFoods, modalFoodId])
  const selectedRecipe: QuantifiedFood | undefined = useMemo(() => {
    const recipe = userRecipes.find(({ id }) => id === modalRecipeId)
    if (!recipe) return undefined
    return {
      recipe,
      quantity: 1,
      unit: 'PORTION'
    }
  }, [userRecipes, modalRecipeId])

  const Foods: FC = () => {
    if (isInitialLoadingFoods) {
      return <LoadingNotification itemTypeName={itemTypeNames.foods} />
    }

    if (userFoods.length === 0) {
      return <NoEntriesPlaceholder itemTypeName={itemTypeNames.foods} />
    }

    const filteredFoods = !normalizedQuery
      ? userFoods
      : userFoods.filter((food) => food.name.toLowerCase().includes(normalizedQuery))

    if (filteredFoods.length === 0) {
      return <NoResultsPlaceholder itemTypeName={itemTypeNames.foods} />
    }

    return (
      <SectionList<Food>
        sectionList={getSectionListSortedByNormalizedName(
          filteredFoods.map((food) => addNormalizedNameProp(food, food.name))
        )}
        renderCard={(food: Food) => (
          <FoodContainer key={food.id} food={food} onClick={() => onFoodCardClick(food.id)} />
        )}
      />
    )
  }

  const Recipes: FC = () => {
    if (isInitialLoadingRecipes) {
      return <LoadingNotification itemTypeName={itemTypeNames.recipes} />
    }

    if (userRecipes.length === 0) {
      return <NoEntriesPlaceholder itemTypeName={itemTypeNames.recipes} />
    }

    const filteredRecipes = !normalizedQuery
      ? userRecipes
      : userRecipes.filter((recipe) => recipe.name.toLowerCase().includes(normalizedQuery))

    if (filteredRecipes.length === 0) {
      return <NoResultsPlaceholder itemTypeName={itemTypeNames.recipes} />
    }

    return (
      <SectionList<Recipe>
        sectionList={getSectionListSortedByNormalizedName(
          filteredRecipes.map((recipe) => addNormalizedNameProp(recipe, recipe.name))
        )}
        renderCard={(recipe: Recipe) => (
          <RecipeCardContainer key={recipe.id} recipe={recipe} onClick={() => onRecipeCardClick(recipe.id)} />
        )}
      />
    )
  }

  const tabs: TabSwitcherProps = {
    tabs: [
      {
        name: 'foods',
        title: 'Meine Nahrungsmittel',
        renderContent: <Foods />
      },
      {
        name: 'recipes',
        title: 'Meine Rezepte',
        renderContent: <Recipes />
      }
    ]
  }

  return (
    <PageLayout variant="smallcircle">
      <div className="container max-900">
        <div className="row">
          <SidebarUserFood />

          <div className="col-12 col-lg-8">
            <h1>Mein Essen</h1>

            <p>Hier sehen Sie eine Übersicht Ihrer selbst erstellten Nahrungsmittel und Rezepte.</p>

            <div className="mt-3 mb-2">
              <SearchBar onChangeText={setSearchQuery} labelText="In «Mein Essen» suchen …" />
            </div>

            <TabSwitcher tabs={tabs.tabs} />
          </div>
        </div>
      </div>

      <BackToTopButton />

      <FoodModal quantifiedFood={selectedFood} onClose={onFoodModalClose} />

      <RecipeModal quantifiedRecipe={selectedRecipe} onClose={onRecipeModalClose} />
    </PageLayout>
  )
}

export default MyEatablesPage
